import { AppConfig } from '@superbet-group/web.lib.config';
import { capitalize } from '@superbet-group/web.lib.utilities';
import { isDesktop } from '@superbet-group/web.lib.vue-utilities';
import {
    chunk as _chunk,
    partition as _partition,
} from 'lodash';

import { OrderDirection } from '@core/constants';
import Odd from '@core/models/offer/Odd';
import liveBettingSpecifiersFormatter from '@core/services/offer/_private/LiveBettingSpecifiersFormatter';
import groupBy from '@core/utils/dataTransformation/groupBy';
import { BetBuilderMarket } from '@sportOfferFederation/bet-builder/types/BetBuilderMarket';
import { OfferEventCondensed } from '@superbet-group/web.types';

const getOddNameMaxLengthForTwoColumns = () => isDesktop() ? 45 : 20;
const getOddNameMaxLengthForThreeColumns = () => isDesktop() ? 30 : 10;

/**
 * This function will try to group odds with the same special bet value (that's the number, like total
 * points, next to the odd name) into the same group.
 * However, it will opt out of this when we have a case where the odd name is too long to fit in the
 * grid layout. The exact numbers were determined by manual testing.
 */
export function getMarketLines<T extends Pick<Odd, 'name' | 'specialBetValue'>>(odds: T[]): [string, T[]][] {
    const groupedOdds = groupBy(odds, 'specialBetValue');
    const marketLines = Array.from(groupedOdds);
    const hasOddsWithLongNamesForTwoColumns = odds.some((odd) => odd.name.length > getOddNameMaxLengthForTwoColumns());
    const hasOddsWithLongNamesForThreeColumns = odds.some((odd) => odd.name.length > getOddNameMaxLengthForThreeColumns());
    const columnCount = Math.max(...marketLines.map((marketLine) => marketLine[1].length)) ?? 1;

    // Can't fit them in any case, go for single odds
    if (hasOddsWithLongNamesForTwoColumns) {
        return odds.map((odd) => [odd.specialBetValue, [odd]]);
    } else if (hasOddsWithLongNamesForThreeColumns) {
        return columnCount === 2
            ? marketLines
            : _chunk(odds, 2).map((oddsChunk) => [oddsChunk[0].specialBetValue, oddsChunk]);
    } else {
        return marketLines;
    }
}

function isPlayersMarket(market: BetBuilderMarket): boolean {
    const playerKeys = ['player', 'player_id', 'player_name'];
    const filteredMarketOdds = market.odds.filter(({ specifiers }) => specifiers && playerKeys.find((key) => specifiers[key]));
    return filteredMarketOdds.length === market.odds.length;
}

export function getBetBuilderMarketLinesLimit(market: BetBuilderMarket): number | null {
    if (isPlayersMarket(market)) {
        return 6;
    }

    return null;
}

export function sortSuperBets(
    odds: Odd[],
    sortDirection: AppConfig['superbets']['sortDirection'],
): Odd[] {
    let oddsComparator;
    if (sortDirection === OrderDirection.Asc) {
        oddsComparator = (a: Odd, b: Odd) => a.value - b.value;
    } else {
        oddsComparator = (a: Odd, b: Odd) => b.value - a.value;
    }
    return odds.sort(oddsComparator);
}

export function getFeaturedSuperBets(odds: Odd[], numberOfFeaturedSuperBets: number): Odd[] {
    const [highlightedSuperBets, others] = _partition(odds, (odd) => odd.isHighlightedSuperBet);
    return [...highlightedSuperBets, ...others].slice(0, numberOfFeaturedSuperBets);
}

export function isMarketTypeFilterVisible(
    odds: Odd[],
    options: {
        isEnabled: boolean;
        availableBetTypesInStruct: number;
        numberOfFeaturedSuperBets: number;
        numberOfBetTypes: number;
        minimalNumberOfBetTypes: number;
    },
): boolean {
    return (
        options.isEnabled
        && options.availableBetTypesInStruct > 0
        && options.numberOfBetTypes >= options.minimalNumberOfBetTypes
        && odds.length > options.numberOfFeaturedSuperBets
    );
}

export function isOddsRangeSliderVisible(odds: Odd[], numberOfFeaturedSuperBets: number): boolean {
    return odds.length > numberOfFeaturedSuperBets;
}

export function isShowMoreVisible(odds: Odd[], numberOfFeaturedSuperBets: number): boolean {
    return odds.length > numberOfFeaturedSuperBets;
}

export function getSuperBetFormatter(specifiers: Record<string, string>): (part: string) => string {
    return (part) => capitalize(liveBettingSpecifiersFormatter.format(part, specifiers));
}

/**
 * [Notion link](https://www.notion.so/superbet/Per-Leg-Settlement-clients-Technical-specification-Phase-I-core-experience-13d032f852c5805e81cac7c93fbdc55c?pvs=4#13d032f852c5804696b2c641d1cee2a5)
 */
export const validateEventComponents = (eventComponents: OfferEventCondensed['eventComponents']): boolean => {
    if (!eventComponents?.length) {
        return false;
    }

    return eventComponents.every(({ market, oddComponent }) => Boolean(market.name) && Boolean(oddComponent.name) && Boolean(oddComponent.oddStatus));
};

export function getSuperBetParts(name: string, specifiers: Record<string, string>): string[] {
    return name
        .split(';')
        .map((part) => part.trim())
        .map(getSuperBetFormatter(specifiers));
}

export function filterVisibleSuperBets(
    odds: Odd[],
    from: number,
    to: number,
    maxOddsRangeValue: number,
    options: { isOddsRangeSliderVisible: boolean },
): Odd[] {
    let internalOdds = odds;

    // Filter by odds range slider
    if (options.isOddsRangeSliderVisible) {
        internalOdds = odds.filter(
            (odd) =>
                (odd.value >= from && odd.value < to)
                || (to === maxOddsRangeValue && odd.value === maxOddsRangeValue),
        );
    }

    return internalOdds;
}
